import { useState } from 'react';
import { notification } from 'antd';
import instance from '@src/api/axiosInstance';
import { isSharedView } from '../NexaStore';
import { logger } from '@src/utils/misc/logger';

interface UseDownloadReportReturn {
  downloadImage: (sessionId: string, versionId: number, sessionName?: string) => Promise<void>;
  isDownloading: boolean;
  error: string | null;
}

interface DownloadUrlResponse {
  url: string;
  status: 'reused' | 'generated';
}

export const useDownloadReport = (): UseDownloadReportReturn => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const downloadImage = async (sessionId: string, versionId: number, sessionName?: string) => {
    setIsDownloading(true);
    setError(null);

    try {
      const startTime = Date.now();
      const isShared = isSharedView();
      const endpoint = isShared
        ? `/nexa/report/shared/${versionId}/download-url`
        : `/nexa/report/${versionId}/download-url`;
      const params = isShared
        ? { share_id: sessionId, type: 'png', ...(sessionName && { session_name: sessionName }) }
        : { session_id: sessionId, type: 'png', ...(sessionName && { session_name: sessionName }) };

      // Get signed URL from middleware
      const response = await instance.get<DownloadUrlResponse>(endpoint, {
        params,
      });

      const { url, status } = response.data;

      const timeTaken = Date.now() - startTime;

      logger.log(`[Report Download] Request completed in ${timeTaken}ms`, {
        versionId,
        sessionId,
        status,
        isShared,
        timeTakenMs: timeTaken,
      });

      // Trigger browser download via signed URL with responseDisposition
      // The backend sets 'Content-Disposition: attachment' in the signed URL
      // which forces the browser to download instead of displaying
      const link = document.createElement('a');
      link.href = url;
      const fallbackName = sessionName
        ? `${sessionName.replace(/[^a-zA-Z0-9_\-\s]/g, '').replace(/\s+/g, '_')}_v${versionId}.png`
        : `report_v${versionId}.png`;
      link.download = fallbackName;
      link.style.display = 'none';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      notification.success({
        message: 'Download starting...',
        description: 'Generated successfully, your report is downloading...',
      });
    } catch (err: any) {
      let errorMessage = 'Failed to download report. Please try again.';

      // Handle specific error cases
      if (err?.response?.status === 503) {
        errorMessage = 'Service is busy. Please try again in a few moments.';
      } else if (err?.response?.status === 504) {
        errorMessage = 'Download took too long. Please try again.';
      } else if (err?.response?.data?.message) {
        errorMessage = err.response.data.message;
      }

      setError(errorMessage);
      notification.error({
        message: 'Download failed',
        description: errorMessage,
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return { downloadImage, isDownloading, error };
};
